import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Interaction } from '../../model/common/interaction.model';
import { SelectionMode } from '../../model/common/selection-mode.enum';
import { ClassifierMessage } from '../../model/mail-classifier/classifier-message.model';
import { SdApiService } from '../../services/base/sd-api.service';
import { ComposerMailService } from '../../services/composer-mail.service';
import { ConfigurationService } from '../../services/configuration.service';
import { MailReviewService } from '../../services/mail-review.service';
import { SnackBarService } from '../../services/snackbar/snackbar.service';
import { MailComposerMinComponent } from '../home/mail-composer-min/mail-composer-min.component';
import { ClassifiedEmail, RecentClassification } from '../../model/classification-history/recent-classification.model';
import { RecentClassificationDto } from '../../model/classification-history/recent-classification.dto';
import { ClassificationHistoryService } from '../../services/classification-history.service';

@Component({
  selector: 'app-classification-history',
  templateUrl: './classification-history.component.html',
  styleUrl: './classification-history.component.scss',
  standalone: false,
})
export class ClassificationHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  selectionMode: SelectionMode = SelectionMode.SINGLE;
  currentMessage: ClassifierMessage = null;
  selectedInteraction: Interaction = null;
  classifiedEmails: ClassifiedEmail[] = [];
  pageSize = 5;
  total = 0;
  isLoadingClassifiedEmails = false;
  isLoadingEmail = false;

  columnHeaders = ['SDR Id', 'SDR Email', 'Classification', 'Client Id', 'Client Name'];
  displayedColumns = ['sdrId', 'sdrEmail', 'classification', 'clientId', 'clientName'];
  elementsToCalculateHeight = ['table-container', 'details-toolbar'];
  private destroy$ = new Subject<boolean>();

  constructor(
    private composerMailService: ComposerMailService,
    private configurationService: ConfigurationService,
    private matDialog: MatDialog,
    private mailReviewService: MailReviewService,
    private sdApiService: SdApiService,
    private classificationHistoryService: ClassificationHistoryService,
    private snackBarService: SnackBarService,
  ) {}

  ngOnInit() {
    this.fetchRecentClassifiedEmails(0, this.pageSize);
    this.sdApiService.loadUserClientsSDRs();
    this.configurationService.loadCustomers();

    this.mailReviewService.selectedInteraction$.pipe(takeUntil(this.destroy$)).subscribe((interaction) => {
      this.selectedInteraction = interaction;

      if (this.selectedInteraction) {
        this.getClientRepresentatives();
      } else {
        this.configurationService.setClientRepresentatives(null);
      }
    });

    this.mailReviewService.currentClassifierMessage$.pipe(takeUntil(this.destroy$)).subscribe((message) => {
      this.currentMessage = message;
    });
  }

  ngAfterViewInit(): void {
    this.composerMailService.isMailComposerMediumOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => isOpen && this.openMediumDialog());

    this.composerMailService.isMailComposerMinimized
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMinimized) => isMinimized && this.openMinimizedDialog());
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  getClientRepresentatives() {
    this.configurationService.getClientRepresentatives(this.selectedInteraction.sdrId);
  }

  clearSearchValues() {
    this.mailReviewService.setClickedEmail('');
    this.mailReviewService.setClickedProspectId(null);
    this.mailReviewService.setSearchParams(null);
  }

  setClassifierMessage(classifierMessage: ClassifierMessage) {
    this.matDialog.closeAll();
    this.mailReviewService.setClassifierMessage(classifierMessage || null);
  }

  setEmailToGetInteractions(email: string) {
    this.mailReviewService.setClickedEmail(email);
  }

  openMediumDialog() {
    const dialogData = JSON.parse(window.localStorage.getItem('fwdEmailData') || '{}');
    const dialModalRef = this.matDialog.open(MailComposerMinComponent, {
      panelClass: 'mail-composer-container',
      hasBackdrop: false,
      data: dialogData,
      autoFocus: false,
      disableClose: true,
      maxHeight: '98vh',
    });
    dialModalRef.updatePosition({ bottom: '0', right: '20px' });
  }

  openMinimizedDialog() {
    const dialogData = JSON.parse(window.localStorage.getItem('fwdEmailData') || '{}');

    this.matDialog.open(MailComposerMinComponent, {
      panelClass: 'mail-composer-container',
      hasBackdrop: false,
      data: dialogData,
      autoFocus: false,
      disableClose: true,
      width: '400px',
      height: '36px',
      position: { bottom: '0', right: '20px' },
    });
  }

  fetchRecentClassifiedEmails(pageIndex: number, pageSize: number) {
    this.isLoadingClassifiedEmails = true;
    const recentClassificationDto: RecentClassificationDto = { page: pageIndex + 1, pageSize: pageSize };

    this.classificationHistoryService
      .getRecentClassifiedEmails(recentClassificationDto)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingClassifiedEmails = false)),
      )
      .subscribe({
        next: (response: RecentClassification) => {
          this.classifiedEmails = response.recentClassifiedEmails || [];
          this.total = response.totalRecords;
        },
        error: () => {
          this.snackBarService.showError('An error occurred while fetching recent classified emails');
          this.isLoadingClassifiedEmails = false;
        },
      });
  }

  onPageChange({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) {
    this.setClassifierMessage(null);
    this.clearSearchValues();
    this.fetchRecentClassifiedEmails(pageIndex, pageSize);
  }

  onSelectedChange(selected: any[]) {
    const selectedEmail: ClassifiedEmail = selected[0];
    if (!selectedEmail) {
      this.setClassifierMessage(null);
      return;
    }
    this.clearSearchValues();

    this.isLoadingEmail = true;
    this.classificationHistoryService
      .getClassifiedEmail(selectedEmail?.messageHandlerId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingEmail = false)),
      )
      .subscribe({
        next: (response: ClassifierMessage) => {
          if (!response) {
            this.setClassifierMessage(null);
            this.snackBarService.showError('No classified email found');
            return;
          }
          this.currentMessage = response;
          this.setClassifierMessage(this.currentMessage);

          const fromEmail = this.currentMessage?.email?.fromMail;

          this.setEmailToGetInteractions(fromEmail || '');
        },
        error: () => {
          this.setClassifierMessage(null);
          this.snackBarService.showError('An error occurred while fetching the classified email');
        },
      });
  }

  onRefresh() {
    this.fetchRecentClassifiedEmails(0, this.pageSize);
    this.clearSearchValues();
  }
}
